import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_PREFIX } from '../services/api';

import {
  Box,
  Typography,
  Tabs,
  Tab,
  GridLegacy as Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  Chip,
  LinearProgress,
  Pagination,
  Skeleton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Cancel as RejectedIcon,
  Pending as PendingIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  Assignment as AssignmentIcon,
  Feedback as FeedbackIcon,
  CalendarToday as CalendarIcon,
  Work,
  CheckCircle,
  Refresh as RefreshIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  FirstPage,
  LastPage,
  Search,
  LocationOn,
  Assessment,
  Clear,
  MoreVert,
  Business,
  People,
  Assignment as CandidatesIcon,
  AttachMoney,
  Warning,
  Lightbulb,
  Notes,
} from '@mui/icons-material';

import InterviewManagement from '../components/interview/InterviewManagement';
import { format, isValid } from 'date-fns';
import { panelistAPI } from '../services/api';
import { useAuth } from '../utils/AuthContext';
import TalentFlowCalendar from '../components/shared/TalentFlowCalendar';
import AvailabilityManagement from '../components/panelist/AvailabilityManagement';
import AppShell from '../components/shared/AppShell';
import ResumeActions from '../components/shared/ResumeViewer';
import { Section } from '../components/shared/ui';

const PanelistDashboard = () => {
  const { user } = useAuth();

  // ===== DASHBOARD STATE =====
  const [activeTab, setActiveTab] = useState(0);
  



  const [dashboardData, setDashboardData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // ===== ENHANCED CV REVIEW STATE WITH ADVANCED PAGINATION =====
  const [pendingReviews, setPendingReviews] = useState([]);
  const [reviewsPagination, setReviewsPagination] = useState({
    page: 1,
    per_page: 12,
    total: 0,
    pages: 1,
    has_next: false,
    has_prev: false,
    showing_start: 0,
    showing_end: 0
  });
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Removed sort state declarations as per request

  // ===== ENHANCED FEEDBACK HISTORY STATE WITH ADVANCED PAGINATION =====
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 1,
    has_next: false,
    has_prev: false,
    showing_start: 0,
    showing_end: 0
  });
  const [historyLoading, setHistoryLoading] = useState(false);

  // ===== INTERVIEW FEEDBACK HISTORY STATE WITH ADVANCED PAGINATION =====
  const [interviewHistory, setInterviewHistory] = useState([]);
  const [interviewHistoryPagination, setInterviewHistoryPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 1,
    has_next: false,
    has_prev: false,
    showing_start: 0,
    showing_end: 0
  });
  const [interviewHistoryLoading, setInterviewHistoryLoading] = useState(false);

  // ===== ENHANCED INTERVIEW HISTORY WITH ADVANCED PAGINATION =====
  const loadInterviewHistory = useCallback(async (page = 1) => {
    setInterviewHistoryLoading(true);
    console.log('🔄 Loading interview feedback history...', { page });

    const params = {
      page,
      per_page: interviewHistoryPagination.per_page
    };

    const data = await safeApiCall(
      () => panelistAPI.getInterviewFeedbackHistory(params),
      'Loading interview feedback history',
      { interview_history: [], pagination: {} }
    );

    if (data && data.interview_history) {
      // Sort interview history by most recent first (completed_at descending)
      const sortedHistory = [...data.interview_history].sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));
      setInterviewHistory(sortedHistory);
      updatePaginationState(setInterviewHistoryPagination, data, page);
      console.log(`✅ Interview feedback history loaded: ${sortedHistory.length} items`);
    }

    setInterviewHistoryLoading(false);
  }, [interviewHistoryPagination.per_page]);

  // ===== HISTORY SUB-TAB STATE =====
  const [historySubTab, setHistorySubTab] = useState('cv');

  // ===== ENHANCED JOBS PAGINATION =====
  const [jobsPagination, setJobsPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 1,
    has_next: false,
    has_prev: false,
    showing_start: 0,
    showing_end: 0
  });
  const [jobsLoading, setJobsLoading] = useState(false);
  
  // ===== JOB MANAGEMENT STATE (NEW) =====
  const [jobsFilter, setJobsFilter] = useState({
    search: '',
    status: 'all',
    priority: 'all',
    location: ''
  });
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetailsOpen, setJobDetailsOpen] = useState(false);

  // ===== REVIEW DIALOG STATE =====
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [isReadOnlyView, setIsReadOnlyView] = useState(false);

  // ===== FEEDBACK DISPLAY DIALOG STATE =====
  const [feedbackDisplayDialogOpen, setFeedbackDisplayDialogOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedback, setFeedback] = useState({
    overall_rating: 0,
    status: '',
    experience_relevance: 0,
    education_fit: 0,
    domain_knowledge: 0,
    overall_impression: 0,
    strengths: '',
    weaknesses: '',
    recommendations: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });

  // ===== UTILITY FUNCTIONS =====
  const showAlert = (message, severity) => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ open: false, message: '', severity: 'info' }), 5000);
  };

  // One source of truth for navigation, consumed by the shell for both the
  // desktop rail and the mobile drawer.
  //
  // NOTE: this order must match the `activeTab === n` content panels below.
  // It previously listed Interviews/Availability/History as 3/4/5 while the
  // panels (and the desktop tab bar) used History/Interviews/Availability, so
  // choosing "Interviews" on mobile opened the History panel. Deriving both
  // navigations from this single array is what prevents that from recurring.
  const tabsConfig = [
    { label: 'Overview', icon: <Assessment /> },
    { label: 'CV Reviews', icon: <AssignmentIcon />, badge: reviewsPagination.total || 0 },
    { label: 'Jobs', icon: <Work /> },
    { label: 'History', icon: <FeedbackIcon /> },
    { label: 'Interviews', icon: <ScheduleIcon /> },
    { label: 'Availability', icon: <CalendarIcon /> },
  ];

  const changeTabTo = (idx) => {
    setActiveTab(idx);
  };


  // ===== ENHANCED API ERROR HANDLER =====
  const handleApiError = (error, context) => {
    console.error(`❌ ${context} error:`, error);
    if (error.message && error.message.includes('401')) {
      showAlert('Session expired. Please login again.', 'error');
      return;
    }
    showAlert(`Failed to ${context.toLowerCase()}. Please try again.`, 'error');
  };

  // ===== ENHANCED SAFE API CALL WITH BETTER ERROR HANDLING =====
  const safeApiCall = async (apiCall, context, fallbackData) => {
    try {
      console.log(`🔄 ${context}...`);
      const response = await apiCall();
      console.log(`✅ ${context} successful:`, response.data);
      return response.data;
    } catch (error) {
      handleApiError(error, context);
      return fallbackData;
    }
  };

  // ===== ENHANCED PAGINATION HELPER FUNCTIONS =====
  const calculateShowingRange = (page, perPage, total) => {
    const start = Math.min((page - 1) * perPage + 1, total);
    const end = Math.min(page * perPage, total);
    return { showing_start: start, showing_end: end };
  };

  const updatePaginationState = (setPagination, data, page) => {
    const pagination = data.pagination || {};
    const range = calculateShowingRange(page, pagination.per_page || 10, pagination.total || 0);
    
    setPagination(prev => ({
      ...prev,
      page,
      per_page: pagination.per_page || prev.per_page,
      total: pagination.total || 0,
      pages: pagination.pages || Math.ceil((pagination.total || 0) / (pagination.per_page || prev.per_page)),
      has_next: pagination.has_next || page < Math.ceil((pagination.total || 0) / (pagination.per_page || prev.per_page)),
      has_prev: pagination.has_prev || page > 1,
      ...range
    }));
  };

  // ===== ENHANCED PAGINATION NAVIGATION COMPONENTS =====
  const PaginationControls = ({ pagination, onPageChange, loading, label }) => {
    if (pagination.total <= pagination.per_page) return null;

    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mt: 3, 
        p: 2, 
        bgcolor: 'grey.50', 
        borderRadius: 1,
        border: '1px solid #e0e0e0'
      }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Showing {pagination.showing_start}-{pagination.showing_end} of {pagination.total} {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Page {pagination.page} of {pagination.pages}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={!pagination.has_prev || loading}
            onClick={() => onPageChange(1)}
            startIcon={<FirstPage />}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            First
          </Button>
          
          <Button
            variant="outlined"
            size="small"
            disabled={!pagination.has_prev || loading}
            onClick={() => onPageChange(pagination.page - 1)}
            startIcon={<KeyboardArrowLeft />}
          >
            Previous
          </Button>
          
          <Box sx={{ mx: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
              const pageNum = Math.max(1, pagination.page - 2) + i;
              if (pageNum > pagination.pages) return null;
              
              return (
                <Button
                  key={pageNum}
                  variant={pageNum === pagination.page ? "contained" : "outlined"}
                  size="small"
                  onClick={() => onPageChange(pageNum)}
                  disabled={loading}
                  sx={{ minWidth: 40, height: 32 }}
                >
                  {pageNum}
                </Button>
              );
            })}
          </Box>
          
          <Button
            variant="outlined"
            size="small"
            disabled={!pagination.has_next || loading}
            onClick={() => onPageChange(pagination.page + 1)}
            endIcon={<KeyboardArrowRight />}
          >
            Next
          </Button>
          
          <Button
            variant="outlined"
            size="small"
            disabled={!pagination.has_next || loading}
            onClick={() => onPageChange(pagination.pages)}
            endIcon={<LastPage />}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            Last
          </Button>
        </Box>
      </Box>
    );
  };

  // ... [Keep all the existing load data functions, handlers, and utility functions as they are] ...

  // ===== LOAD DATA FUNCTIONS =====
  const loadDashboardData = useCallback(async () => {
    const data = await safeApiCall(
      () => panelistAPI.getDashboard(),
      'Loading panelist dashboard data',
      {
        assigned_jobs: [],
        pending_reviews: [],
        statistics: { overview: {}, cv_review_performance: {}, interview_performance: {}, recent_activity: {}, impact_metrics: {} },
        summary: { total_assigned_jobs: 0, pending_reviews_count: 0, high_priority_reviews: 0, upcoming_interviews_count: 0, interviews_today: 0, overdue_reviews: 0 }
      }
    );

    if (data) {
      setDashboardData(data);
    }
  }, []);

  // ===== ENHANCED PENDING REVIEWS WITH ADVANCED PAGINATION =====
  const loadPendingReviews = useCallback(async (page = 1) => {
    setReviewsLoading(true);
    console.log('🔄 Loading pending CV reviews...', { page });

    const params = {
      page,
      per_page: reviewsPagination.per_page
    };

    const data = await safeApiCall(
      () => panelistAPI.getPendingCVReviews(params),
      'Loading pending CV reviews',
      { pending_reviews: [], pagination: {}, summary: {} }
    );

    if (data && data.pending_reviews) {
      // Sort pending reviews by most recent first (submitted_at descending)
      const sortedReviews = [...data.pending_reviews].sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
      setPendingReviews(sortedReviews);
      updatePaginationState(setReviewsPagination, data, page);
      console.log(`✅ Pending reviews loaded: ${sortedReviews.length} items`);
    } else if (dashboardData?.pending_reviews) {
      // Enhanced fallback with pagination simulation
      const sortedReviews = [...dashboardData.pending_reviews];
      sortedReviews.sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));

      const startIdx = (page - 1) * reviewsPagination.per_page;
      const endIdx = startIdx + reviewsPagination.per_page;
      const paginatedReviews = sortedReviews.slice(startIdx, endIdx);

      setPendingReviews(paginatedReviews);

      const total = sortedReviews.length;
      const pages = Math.ceil(total / reviewsPagination.per_page);
      const range = calculateShowingRange(page, reviewsPagination.per_page, total);

      setReviewsPagination(prev => ({
        ...prev,
        page,
        total,
        pages,
        has_next: page < pages,
        has_prev: page > 1,
        ...range
      }));

      console.log('⚠️ Using dashboard fallback data for pending reviews with pagination');
    }

    setReviewsLoading(false);
  }, [reviewsPagination.per_page, dashboardData]);

  // ===== ENHANCED FEEDBACK HISTORY WITH ADVANCED PAGINATION =====
  const loadFeedbackHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    console.log('🔄 Loading feedback history...', { page });

    const params = {
      page,
      per_page: historyPagination.per_page
    };

    const data = await safeApiCall(
      () => panelistAPI.getFeedbackHistory(params),
      'Loading feedback history',
      { feedback_history: [], pagination: {} }
    );

    if (data && data.feedback_history) {
      // Sort feedback history by most recent first (submitted_at descending)
      const sortedHistory = [...data.feedback_history].sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
      setFeedbackHistory(sortedHistory);
      updatePaginationState(setHistoryPagination, data, page);
      console.log(`✅ Feedback history loaded: ${sortedHistory.length} items`);
    }

    setHistoryLoading(false);
  }, [historyPagination.per_page]);

  // ===== ENHANCED JOBS WITH ADVANCED PAGINATION =====
  const loadJobs = useCallback(async (page = 1) => {
    setJobsLoading(true);
    console.log('🔄 Loading jobs...', { page });

    const params = { 
      page, 
      per_page: jobsPagination.per_page 
    };

    const data = await safeApiCall(
      () => panelistAPI.getAssignedJobs(params),
      'Loading assigned jobs',
      { jobs: [], pagination: {} }
    );

    if (data) {
      const jobsData = data.jobs || [];
      setJobs(jobsData);
      
      if (data.pagination) {
        updatePaginationState(setJobsPagination, data, page);
      } else {
        // Fallback pagination calculation
        const total = jobsData.length;
        const pages = Math.ceil(total / jobsPagination.per_page);
        const range = calculateShowingRange(page, jobsPagination.per_page, total);
        
        setJobsPagination(prev => ({
          ...prev,
          page,
          total,
          pages,
          has_next: page < pages,
          has_prev: page > 1,
          ...range
        }));
      }
    }

    setJobsLoading(false);
  }, [jobsPagination.per_page]);

  // ===== JOB FILTERING LOGIC =====
  // ===== ENHANCED PAGINATION HANDLERS =====
  const handleReviewsPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= reviewsPagination.pages && !reviewsLoading) {
      loadPendingReviews(newPage);
    }
  };

  const handleHistoryPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= historyPagination.pages && !historyLoading) {
      loadFeedbackHistory(newPage);
    }
  };

  const handleInterviewHistoryPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= interviewHistoryPagination.pages && !interviewHistoryLoading) {
      loadInterviewHistory(newPage);
    }
  };
  const clearJobFilters = () => {
    setJobsFilter({
      search: '',
      status: 'all',
      priority: 'all',
      location: ''
    });
  };

  const handleJobsPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= jobsPagination.pages && !jobsLoading) {
      loadJobs(newPage);
    }
  };

  // Removed sort change handlers as per request

  // ===== JOB MANAGEMENT HANDLERS =====
  const handleViewJobDetails = (job) => {
    setSelectedJob(job);
    setJobDetailsOpen(true);
  };

  // ===== ENHANCED REFRESH FUNCTIONS =====
  const refreshReviews = () => {
    loadPendingReviews(reviewsPagination.page);
  };

  const refreshHistory = () => {
    loadFeedbackHistory(historyPagination.page);
  };

  const refreshJobs = () => {
    loadJobs(jobsPagination.page);
  };

  const refreshInterviewHistory = () => {
    loadInterviewHistory(interviewHistoryPagination.page);
  };

  const refreshAll = async () => {
    setLoading(true);
    await Promise.all([
      loadDashboardData(),
      loadPendingReviews(reviewsPagination.page),
      loadFeedbackHistory(historyPagination.page),
      loadInterviewHistory(interviewHistoryPagination.page),  // ✅ FIXED: Include interview history in refresh
      loadJobs(jobsPagination.page)
    ]);
    setLoading(false);
    showAlert('Dashboard refreshed successfully!', 'success');
  };

  // ===== LOAD DATA ON MOUNT =====
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      await loadDashboardData();
      await Promise.all([
        loadJobs(1),
        loadPendingReviews(1),
        loadFeedbackHistory(1),
        loadInterviewHistory(1)  // ✅ FIXED: Load interview feedback history on mount
      ]);
      setLoading(false);
    };

    loadAllData();
  }, []);

  // ===== CV REVIEW FUNCTIONS =====
  const handleReviewCandidate = async (candidateId, readOnly = false) => {
    const data = await safeApiCall(
      () => panelistAPI.getCandidateCVDetails(candidateId),
      'Loading candidate details for review',
      null
    );

    if (!data || !data.candidate) {
      return;
    }

    setSelectedCandidate(data.candidate);
    setIsReadOnlyView(readOnly);

    if (data.candidate.existing_feedback) {
      const existing = data.candidate.existing_feedback;
      setFeedback({
        overall_rating: existing.rating || 0,
        status: existing.status || '',
        experience_relevance: existing.experience_relevance || 0,
        education_fit: existing.education_fit || 0,
        domain_knowledge: existing.domain_knowledge || 0,
        overall_impression: existing.overall_impression || 0,
        strengths: existing.strengths || '',
        weaknesses: existing.weaknesses || '',
        recommendations: existing.recommendations || '',
        notes: existing.notes || ''
      });
    } else {
      setFeedback({
        overall_rating: 0,
        status: '',
        experience_relevance: 0,
        education_fit: 0,
        domain_knowledge: 0,
        overall_impression: 0,
        strengths: '',
        weaknesses: '',
        recommendations: '',
        notes: ''
      });
    }

    setReviewDialogOpen(true);
  };

   // ===== FEEDBACK DISPLAY FUNCTION =====
   const handleViewFeedback = async (feedback) => {
     // The feedback object now contains complete data from the backend
     // No need to make additional API call since we have all the data
     setSelectedFeedback(feedback);
     setFeedbackDisplayDialogOpen(true);
   };
const handleViewInterviewFeedback = (interview) => {
  setSelectedFeedback(interview);
  setFeedbackDisplayDialogOpen(true);
};


  const handleSubmitFeedback = async () => {
    if (!feedback.overall_rating || feedback.overall_rating < 1 || feedback.overall_rating > 5) {
      showAlert('Overall rating must be between 1 and 5', 'error');
      return;
    }

    if (!feedback.status) {
      showAlert('Please select a status', 'error');
      return;
    }

    setSubmitting(true);
    const validatedFeedback = {
      ...feedback,
      overall_rating: Number(feedback.overall_rating),
      experience_relevance: Number(feedback.experience_relevance) || 0,
      education_fit: Number(feedback.education_fit) || 0,
      domain_knowledge: Number(feedback.domain_knowledge) || 0,
      overall_impression: Number(feedback.overall_impression) || 0,
      strengths: (feedback.strengths || '').trim(),
      weaknesses: (feedback.weaknesses || '').trim(),
      recommendations: (feedback.recommendations || '').trim(),
      notes: (feedback.notes || '').trim()
    };

    const success = await safeApiCall(
      () => panelistAPI.submitComprehensiveFeedback(selectedCandidate.id, validatedFeedback),
      'Submitting comprehensive feedback',
      false
    );

    if (success) {
      showAlert('Comprehensive feedback submitted successfully', 'success');
      setReviewDialogOpen(false);
      await Promise.all([
        loadDashboardData(),
        loadPendingReviews(reviewsPagination.page),
        loadFeedbackHistory(historyPagination.page)
      ]);
    }

    setSubmitting(false);
  };

  // ===== UTILITY FUNCTIONS (UNCHANGED) =====
  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle color="success" />;
      case 'rejected': return <RejectedIcon color="error" />;
      default: return <PendingIcon color="action" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isValid(date) ? format(date, 'MMM dd, yyyy') : 'Invalid Date';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning'; 
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'success';
      case 'filled': return 'info';
      case 'closed': return 'default';
      case 'on_hold': return 'warning';
      default: return 'default';
    }
  };

  // ===== JOBS FILTERED LIST =====
  const filteredJobs = jobs.filter((job) => {
    // Search filter (title, location, description)
    const searchMatch =
      !jobsFilter.search ||
      job.title?.toLowerCase().includes(jobsFilter.search.toLowerCase()) ||
      job.location?.toLowerCase().includes(jobsFilter.search.toLowerCase()) ||
      job.description?.toLowerCase().includes(jobsFilter.search.toLowerCase());

    // Status filter
    const statusMatch =
      jobsFilter.status === 'all' ||
      (job.status && job.status === jobsFilter.status);

    // Priority filter
    const priorityMatch =
      jobsFilter.priority === 'all' ||
      (job.priority && job.priority === jobsFilter.priority);

    // Location filter
    const locationMatch =
      !jobsFilter.location ||
      (job.location && job.location.toLowerCase().includes(jobsFilter.location.toLowerCase()));

    return searchMatch && statusMatch && priorityMatch && locationMatch;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading Panelist Dashboard...</Typography>
      </Box>
    );
  }

  return (
    <AppShell
      role="panelist"
      sections={tabsConfig}
      activeIndex={activeTab}
      onSectionChange={changeTabTo}
      onRefresh={refreshAll}
      loading={loading}
    >
      {/* Transient messages are shown once, in the Snackbar at the end of this
          file. They previously rendered twice simultaneously — inline here and
          again in the Snackbar — because both read the same `alert` state. */}
      <Box>
            <Box>

              {/* ===== OVERVIEW TAB (UNCHANGED) ===== */}
              {activeTab === 0 && dashboardData && (
                <Grid container spacing={3}>
                  {/* Quick Stats */}
                  <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {/* Use whole-number grid columns to avoid gaps */}
                    <Grid item xs={12} sm={6} md={2}>
                      <Card 
                        sx={{ textAlign: 'center', p: 2, cursor: 'pointer' }} 
                        onClick={() => setActiveTab(2)}
                      >
                        <Work sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {dashboardData.summary?.total_assigned_jobs || 0}
                        </Typography>
                        <Typography color="text.secondary">Assigned Jobs</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Card 
                        sx={{ textAlign: 'center', p: 2, cursor: 'pointer' }} 
                        onClick={() => setActiveTab(1)}
                      >
                        <AssignmentIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {dashboardData.summary?.pending_reviews_count || 0}
                        </Typography>
                        <Typography color="text.secondary">Pending Reviews</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Card
                        sx={{ textAlign: 'center', p: 2, cursor: 'pointer' }}
                        onClick={() => setActiveTab(4)}
                      >
                        <ScheduleIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {dashboardData.summary?.upcoming_interviews_count || 0}
                        </Typography>
                        <Typography color="text.secondary">Upcoming Interviews</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card
                        sx={{ textAlign: 'center', p: 2, cursor: 'pointer' }}
                        onClick={() => setActiveTab(3)}
                      >
                        <FeedbackIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          Recent Activity
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          CV Reviews (Last 30 Days): {dashboardData.statistics?.recent_activity?.feedbacks_last_30_days || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Interviews (Last 30 Days): {dashboardData.statistics?.recent_activity?.interviews_last_30_days || 0}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ textAlign: 'center', p: 2 }}>
                        <StarIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          Performance Metrics
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Interview Success Rate: {dashboardData.statistics?.interview_performance?.success_rate || 0}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Hiring Influence: {dashboardData.statistics?.impact_metrics?.hiring_influence_rate || 0}%
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                  </Grid>
                </Grid>
              )}

              {/* ===== ENHANCED CV REVIEWS TAB (UNCHANGED) ===== */}
              {activeTab === 1 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5">
                      Pending CV Reviews ({reviewsPagination.total})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {/* Removed Sort By Select as per request */}
                      <Button
                        variant="outlined"
                        onClick={refreshReviews}
                        disabled={reviewsLoading}
                        startIcon={<RefreshIcon />}
                      >
                        Refresh
                      </Button>
                    </Box>
                  </Box>

                  {reviewsLoading ? (
                    <Card>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              {[1, 2, 3, 4, 5, 6].map((n) => (
                                <TableCell key={n}>
                                  <Skeleton variant="text" width="100%" height={24} />
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <TableRow key={n}>
                                <TableCell>
                                  <Box>
                                    <Skeleton variant="text" width="80%" height={20} />
                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                      <Skeleton variant="rectangular" width={50} height={20} />
                                      <Skeleton variant="rectangular" width={80} height={20} />
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Skeleton variant="text" width="90%" height={20} />
                                </TableCell>
                                <TableCell>
                                  <Skeleton variant="text" width="85%" height={20} />
                                </TableCell>
                                <TableCell>
                                  <Skeleton variant="text" width="75%" height={20} />
                                </TableCell>
                                <TableCell>
                                  <Skeleton variant="text" width="70%" height={20} />
                                </TableCell>
                                <TableCell>
                                  <Skeleton variant="text" width="55%" height={20} />
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Skeleton variant="rectangular" width={70} height={32} />
                                    <Skeleton variant="rectangular" width={50} height={32} />
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  ) : pendingReviews.length === 0 ? (
                    <Card>
                      <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>All Caught Up!</Typography>
                        <Typography color="text.secondary">
                          No pending CV reviews at the moment. Great job!
                        </Typography>
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      <Card>
                        <TableContainer>
                          <Table>
                            <TableHead>
                          <TableRow>
                            <TableCell><strong>Candidate</strong></TableCell>
                            <TableCell><strong>Job Title</strong></TableCell>
                            <TableCell><strong>Email</strong></TableCell>
                            <TableCell><strong>Submitted</strong></TableCell>
                            <TableCell><strong>Vendor</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                          </TableRow>
                          </TableHead>
                          <TableBody>
                            {pendingReviews.map((candidate) => (
                              <TableRow key={candidate.id} hover>
                                <TableCell>
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      {candidate.name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                      <Chip
                                        size="small"
                                        label={candidate.urgency || 'normal'}
                                        color={getUrgencyColor(candidate.urgency)}
                                        variant="outlined"
                                      />
                                      <Chip
                                        size="small"
                                        label={`${candidate.days_waiting || 0} days waiting`}
                                        color={candidate.days_waiting > 3 ? 'error' : 'default'}
                                        variant="filled"
                                      />
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {candidate.job_details?.title}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {candidate.email}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {formatDate(candidate.submitted_at)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {candidate.vendor_info?.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleReviewCandidate(candidate.id)}
                                      startIcon={<ViewIcon />}
                                    >
                                      Review
                                    </Button>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Card>

                      <PaginationControls
                        pagination={reviewsPagination}
                        onPageChange={handleReviewsPageChange}
                        loading={reviewsLoading}
                        label="pending reviews"
                      />
                    </>
                  )}
                </Box>
              )}

              {/* ===== ENHANCED JOB MANAGEMENT TAB ===== */}
              {activeTab === 2 && (
                <Box>
                  {/* Header with actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5">
                      Job Management ({filteredJobs.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={refreshJobs}
                        disabled={jobsLoading}
                        startIcon={<RefreshIcon />}
                      >
                        Refresh
                      </Button>
                    </Box>
                  </Box>

                  {/* Enhanced Filters Section */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Search jobs"
                            placeholder="Title, location, description..."
                            value={jobsFilter.search}
                            onChange={(e) => setJobsFilter(prev => ({ ...prev, search: e.target.value }))}
                            InputProps={{
                              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={jobsFilter.status}
                              label="Status"
                              onChange={(e) => setJobsFilter(prev => ({ ...prev, status: e.target.value }))}
                            >
                              <MenuItem value="all">All Status</MenuItem>
                              <MenuItem value="open">Open</MenuItem>
                              <MenuItem value="closed">Closed</MenuItem>
                              <MenuItem value="filled">Filled</MenuItem>
                              <MenuItem value="on_hold">On Hold</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Priority</InputLabel>
                            <Select
                              value={jobsFilter.priority}
                              label="Priority"
                              onChange={(e) => setJobsFilter(prev => ({ ...prev, priority: e.target.value }))}
                            >
                              <MenuItem value="all">All Priority</MenuItem>
                              <MenuItem value="urgent">Urgent</MenuItem>
                              <MenuItem value="high">High</MenuItem>
                              <MenuItem value="medium">Medium</MenuItem>
                              <MenuItem value="low">Low</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Location"
                            placeholder="Filter by location..."
                            value={jobsFilter.location}
                            onChange={(e) => setJobsFilter(prev => ({ ...prev, location: e.target.value }))}
                            InputProps={{
                              startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Button
                            variant="outlined"
                            onClick={clearJobFilters}
                            startIcon={<Clear />}
                            fullWidth
                          >
                            Clear Filters
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Jobs Table */}
                  {jobsLoading ? (
                    <Card>
                      <CardContent>
                        <Skeleton variant="text" width="100%" height={40} />
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Box key={n} sx={{ display: 'flex', alignItems: 'center', py: 2, borderBottom: '1px solid #eee' }}>
                            <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                            <Box sx={{ flexGrow: 1 }}>
                              <Skeleton variant="text" width="60%" height={24} />
                              <Skeleton variant="text" width="40%" height={20} sx={{ mt: 0.5 }} />
                            </Box>
                            <Skeleton variant="rectangular" width={80} height={32} sx={{ mr: 1 }} />
                            <Skeleton variant="rectangular" width={80} height={32} />
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  ) : filteredJobs.length === 0 ? (
                    <Card>
                      <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <Work sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No Jobs Found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {Object.values(jobsFilter).some(v => v && v !== 'all') 
                            ? 'Try adjusting your filters to see more results.' 
                            : 'No jobs have been assigned to you yet.'}
                        </Typography>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Job Details</strong></TableCell>
                              <TableCell><strong>Status & Priority</strong></TableCell>
                              <TableCell><strong>Location</strong></TableCell>
                              <TableCell><strong>Candidates</strong></TableCell>
                              <TableCell><strong>My Reviews</strong></TableCell>
                              <TableCell><strong>Created</strong></TableCell>
                              <TableCell><strong>Actions</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredJobs.map((job) => (
                              <TableRow key={job.id} hover>
                                <TableCell>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Job Code: {job.job_code}
                                    </Typography>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      {job.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{
                                      maxWidth: 300,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical'
                                    }}>
                                      {job.description}
                                    </Typography>
                                    {job.salary_range && (
                                      <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <AttachMoney sx={{ fontSize: 16, mr: 0.5 }} />
                                        {job.salary_range}
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Chip
                                      size="small"
                                      label={job.status || 'open'}
                                      color={getStatusColor(job.status)}
                                      variant="filled"
                                    />
                                    <Chip
                                      size="small"
                                      label={job.priority || 'medium'}
                                      color={getPriorityColor(job.priority)}
                                      variant="outlined"
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LocationOn sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                                    <Typography variant="body2">
                                      {job.location || 'Not specified'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CandidatesIcon sx={{ fontSize: 16, mr: 0.5, color: 'info.main' }} />
                                    <Typography variant="body2" fontWeight="bold" color="info.main">
                                      {job.candidate_count || 0}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <FeedbackIcon sx={{ fontSize: 16, mr: 0.5, color: 'success.main' }} />
                                    <Typography variant="body2" fontWeight="bold" color="success.main">
                                      {job.my_reviews_count || 0}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {formatDate(job.created_at)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Tooltip title="View Job Details">
                                      <IconButton 
                                        size="small" 
                                        color="primary"
                                        onClick={() => handleViewJobDetails(job)}
                                      >
                                        <ViewIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  )}

                  <PaginationControls
                    pagination={jobsPagination}
                    onPageChange={handleJobsPageChange}
                    loading={jobsLoading}
                    label="jobs"
                  />
                </Box>
              )}


              {/* ===== FEEDBACK HISTORY TAB ===== */}
              {activeTab === 3 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5">
                      Feedback History
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          if (historySubTab === 'cv') {
                            refreshHistory();
                          } else {
                            refreshInterviewHistory();
                          }
                        }}
                        disabled={historyLoading || interviewHistoryLoading}
                        startIcon={<RefreshIcon />}
                      >
                        Refresh
                      </Button>
                    </Box>
                  </Box>

                  {/* Sub-tabs for CV and Interview feedback */}
                  <Tabs
                    value={historySubTab}
                    onChange={(e, newValue) => {
                      setHistorySubTab(newValue);
                      // ✅ ENHANCEMENT: Ensure interview history loads when switching to interview tab
                      if (newValue === 'interview' && interviewHistory.length === 0 && !interviewHistoryLoading) {
                        loadInterviewHistory(1);
                      }
                    }}
                    sx={{ mb: 3 }}
                  >
                    <Tab label="CV Feedback" value="cv" />
                    <Tab label="Interview Feedback" value="interview" />
                  </Tabs>

                  {historySubTab === 'cv' ? (
                    historyLoading ? (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              {['Candidate', 'Job', 'Rating', 'Status', 'Date', 'Actions'].map((header) => (
                                <TableCell key={header}><Skeleton width="80%" /></TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <TableRow key={n}>
                                {[1, 2, 3, 4, 5, 6].map((cell) => (
                                  <TableCell key={cell}>
                                    <Skeleton width="90%" />
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : feedbackHistory.length === 0 ? (
                      <Card>
                        <CardContent sx={{ textAlign: 'center', py: 6 }}>
                          <FeedbackIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No CV Feedback History
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Your submitted CV feedback will appear here once you start reviewing candidates.
                          </Typography>
                        </CardContent>
                      </Card>
                    ) : (
                      <>
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Candidate</strong></TableCell>
                                <TableCell><strong>Job</strong></TableCell>
                                <TableCell><strong>Rating</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Submitted</strong></TableCell>
                                <TableCell><strong>Actions</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {feedbackHistory.map((feedback) => (
                                <TableRow key={feedback.id} hover>
                                  <TableCell>
                                    <Box>
                                      <Typography variant="subtitle2">
                                        {feedback.candidate_name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {feedback.candidate_email}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {feedback.job_title}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      { [
                                        ...[1, 2, 3, 4, 5].map((star) => (
                                          <StarIcon
                                            key={star}
                                            sx={{
                                              color: star <= (feedback.rating || 0) ? 'warning.main' : 'grey.300',
                                              fontSize: 16
                                            }}
                                          />
                                        )),
                                        <Typography key="rating-text" variant="body2" sx={{ ml: 1 }}>
                                          ({feedback.rating || 0}/5)
                                        </Typography>
                                      ] }
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={feedback.status || 'pending'}
                                      color={feedback.status === 'approved' ? 'success' : feedback.status === 'rejected' ? 'error' : 'default'}
                                      variant="filled"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {formatDate(feedback.submitted_at)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title="View Feedback Details">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleViewFeedback(feedback)}
                                      >
                                        <ViewIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <PaginationControls
                          pagination={historyPagination}
                          onPageChange={handleHistoryPageChange}
                          loading={historyLoading}
                          label="CV feedback entries"
                        />
                      </>
                    )
                  ) : historySubTab === 'interview' ? (
                    interviewHistoryLoading ? (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              {['Candidate', 'Job', 'Rating', 'Status', 'Date', 'Actions'].map((header) => (
                                <TableCell key={header}><Skeleton width="80%" /></TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <TableRow key={n}>
                                {[1, 2, 3, 4, 5, 6].map((cell) => (
                                  <TableCell key={cell}>
                                    <Skeleton width="90%" />
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : interviewHistory.length === 0 ? (
                      <Card>
                        <CardContent sx={{ textAlign: 'center', py: 6 }}>
                          <FeedbackIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Interview Feedback History
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Your submitted interview feedback will appear here once you complete interviews.
                          </Typography>
                        </CardContent>
                      </Card>
                    ) : (
                      <>
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Candidate</strong></TableCell>
                                <TableCell><strong>Job</strong></TableCell>
                                <TableCell><strong>Rating</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Completed</strong></TableCell>
                                <TableCell><strong>Actions</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {interviewHistory.map((interview) => (
  <TableRow key={interview.id} hover>
    <TableCell>
      <Box>
        <Typography variant="subtitle2">
          {interview.candidate_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {interview.candidate_email}
        </Typography>
      </Box>
    </TableCell>
    <TableCell>
      <Typography variant="body2">
        {interview.job_title}
      </Typography>
    </TableCell>
    <TableCell>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            key={star}
            sx={{
              color: star <= (interview.overall_rating || 0) ? 'warning.main' : 'grey.300',
              fontSize: 16
            }}
          />
        ))}
        <Typography key="rating-text" variant="body2" sx={{ ml: 1 }}>
          ({interview.overall_rating || 0}/5)
        </Typography>
      </Box>
    </TableCell>
    <TableCell>
      <Chip
        size="small"
        label={interview.final_verdict || 'pending'}
        color={interview.final_verdict === 'hire' ? 'success' : interview.final_verdict === 'reject' ? 'error' : 'default'}
        variant="filled"
      />
    </TableCell>
    <TableCell>
      <Typography variant="body2" color="text.secondary">
        {formatDate(interview.completed_at)}
      </Typography>
    </TableCell>
    <TableCell>
      <Tooltip title="View Interview Feedback Details">
        <IconButton
          size="small"
          color="primary"
          onClick={() => handleViewInterviewFeedback(interview)}
        >
          <ViewIcon />
        </IconButton>
      </Tooltip>
    </TableCell>
  </TableRow>
))}

                            </TableBody>
                          </Table>
                        </TableContainer>

                        <PaginationControls
                          pagination={interviewHistoryPagination}
                          onPageChange={handleInterviewHistoryPageChange}
                          loading={interviewHistoryLoading}
                          label="interview feedback entries"
                        />
                      </>
                    )
                    ) : null
                  }
                </Box>
              )}

      {/* ===== INTERVIEWS TAB ===== */}
      {activeTab === 4 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Interview Management
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  // Refresh interviews by triggering a re-render of InterviewManagement
                  window.location.reload();
                }}
                disabled={loading}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          <InterviewManagement userRole="panelist" />
        </Box>
      )}

              {/* ===== AVAILABILITY TAB WITH CALENDAR RESTORED ===== */}
              {activeTab === 5 && (
                // Two stacked panels. This was a Grid with spacing={7} (a 56px
                // gutter used nowhere else) whose two children were both lg={12},
                // so the intended side-by-side layout never happened and the
                // gutter became dead vertical space. The page title now comes
                // from the shell, so the duplicate "Availability Management"
                // heading is gone too.
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Section title="Interview slot calendar" disablePadding>
                    <Box sx={{ p: 2 }}>
                      <TalentFlowCalendar
                        userRole="panelist"
                        showInterviewSlots={true}
                        showAvailabilitySlots={true}
                        enableSlotBooking={true}
                        height={640}
                      />
                    </Box>
                  </Section>

                  <Section title="Manage availability" disablePadding>
                    <AvailabilityManagement />
                  </Section>
                </Box>
              )}

            </Box>
          </Box>

      {/* ===== JOB DETAILS DIALOG ===== */}
      <Dialog open={jobDetailsOpen} onClose={() => setJobDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Job Details</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedJob && (
            <Box>
              <Typography variant="h6" gutterBottom>{selectedJob.title}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip
                  size="small"
                  label={selectedJob.status || 'open'}
                  color={getStatusColor(selectedJob.status)}
                />
                <Chip
                  size="small"
                  label={selectedJob.priority || 'medium'}
                  color={getPriorityColor(selectedJob.priority)}
                />
              </Box>
              <Typography variant="body1" paragraph>
                <strong>Description:</strong> {selectedJob.description}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Location:</strong> {selectedJob.location || 'Not specified'}
              </Typography>
              {selectedJob.salary_range && (
                <Typography variant="body1" paragraph>
                  <strong>Salary Range:</strong> {selectedJob.salary_range}
                </Typography>
              )}
              <Typography variant="body1" paragraph>
                <strong>Created:</strong> {formatDate(selectedJob.created_at)}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Total Candidates:</strong> {selectedJob.candidate_count || 0}
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>My Reviews:</strong> {selectedJob.my_reviews_count || 0}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJobDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ===== REVIEW DIALOG ===== */}
      <Dialog open={reviewDialogOpen} onClose={() => {
        setReviewDialogOpen(false);
        setIsReadOnlyView(false);
      }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">
            {isReadOnlyView ? 'View Feedback History' : 'Review Candidate'}
          </Typography>
          {selectedCandidate && (
            <Typography variant="subtitle2" color="text.secondary">
              {selectedCandidate.name} - {selectedCandidate.job_details?.title}
              {isReadOnlyView && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Historical View - Read Only
                </Typography>
              )}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedCandidate && (
            <Box>
              {/* Candidate Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Candidate Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography><strong>Name:</strong> {selectedCandidate.name}</Typography>
                      <Typography><strong>Email:</strong> {selectedCandidate.email}</Typography>
                      <Typography><strong>Phone:</strong> {selectedCandidate.phone || 'Not provided'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography>
                        <strong>Experience:</strong>{' '}
                        {selectedCandidate.total_experience_years ?? selectedCandidate.experience_years ?? 'Not stated'}
                        {(selectedCandidate.total_experience_years ?? selectedCandidate.experience_years) != null ? ' years' : ''}
                      </Typography>
                      <Typography><strong>Submitted:</strong> {formatDate(selectedCandidate.submitted_at)}</Typography>
                      <Typography><strong>Current Stage:</strong> {selectedCandidate.current_stage || 'Applied'}</Typography>
                    </Grid>
                  </Grid>

                  {/* The resume itself — a reviewer cannot assess a candidate
                      from the summary fields alone. */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                    <ResumeActions
                      candidateId={selectedCandidate.id}
                      candidateName={selectedCandidate.name}
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* Feedback Form */}
              <Card sx={{ backgroundColor: '#fafafa', borderRadius: 3, boxShadow: 3, p: 2 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Submit Comprehensive Feedback
                  </Typography>

                  {/* Status */}
                  <Grid item xs={12} md={4} sx={{ mb: 2 }}>
                    <FormControl fullWidth required>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={feedback.status}
                        label="Status"
                        onChange={(e) => !isReadOnlyView && setFeedback(prev => ({ ...prev, status: e.target.value }))}
                        disabled={isReadOnlyView}
                        sx={{ backgroundColor: 'background.paper', maxWidth: 240 }}
                      >
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                        <MenuItem value="needs_clarification">Needs Clarification</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid container spacing={4}>
                    {/* Overall Rating */}
                    <Grid item xs={12}>
                      <Typography gutterBottom variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        Overall Rating (1-5)*
                      </Typography>
                      <Slider
                        value={feedback.overall_rating}
                        onChange={(e, newValue) => !isReadOnlyView && setFeedback(prev => ({ ...prev, overall_rating: newValue }))}
                        disabled={isReadOnlyView}
                        min={1}
                        max={5}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        sx={{ color: 'primary.main' }}
                      />
                    </Grid>

                    {/* Experience Relevance */}
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                        Experience Relevance (0-5)
                      </Typography>
                      <Slider
                        value={feedback.experience_relevance}
                        onChange={(e, newValue) => !isReadOnlyView && setFeedback(prev => ({ ...prev, experience_relevance: newValue }))}
                        disabled={isReadOnlyView}
                        min={0}
                        max={5}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        sx={{ color: 'secondary.main' }}
                      />
                    </Grid>

                    {/* Education Fit */}
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                        Education Fit (0-5)
                      </Typography>
                      <Slider
                        value={feedback.education_fit}
                        onChange={(e, newValue) => !isReadOnlyView && setFeedback(prev => ({ ...prev, education_fit: newValue }))}
                        disabled={isReadOnlyView}
                        min={0}
                        max={5}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        sx={{ color: 'secondary.main' }}
                      />
                    </Grid>

                    {/* Domain Knowledge */}
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                        Domain Knowledge (0-5)
                      </Typography>
                      <Slider
                        value={feedback.domain_knowledge}
                        onChange={(e, newValue) => !isReadOnlyView && setFeedback(prev => ({ ...prev, domain_knowledge: newValue }))}
                        disabled={isReadOnlyView}
                        min={0}
                        max={5}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        sx={{ color: 'secondary.main' }}
                      />
                    </Grid>

                    {/* Overall Impression */}
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                        Overall Impression (0-5)
                      </Typography>
                      <Slider
                        value={feedback.overall_impression}
                        onChange={(e, newValue) => !isReadOnlyView && setFeedback(prev => ({ ...prev, overall_impression: newValue }))}
                        disabled={isReadOnlyView}
                        min={0}
                        max={5}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        sx={{ color: 'secondary.main' }}
                      />
                    </Grid>

                    {/* Strengths */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Strengths"
                        value={feedback.strengths}
                        onChange={(e) => !isReadOnlyView && setFeedback(prev => ({ ...prev, strengths: e.target.value }))}
                        placeholder="What are the candidate's key strengths?"
                        variant="outlined"
                        sx={{ backgroundColor: 'background.paper' }}
                        InputProps={{
                          readOnly: isReadOnlyView
                        }}
                      />
                    </Grid>

                    {/* Weaknesses */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Areas for Improvement"
                        value={feedback.weaknesses}
                        onChange={(e) => !isReadOnlyView && setFeedback(prev => ({ ...prev, weaknesses: e.target.value }))}
                        placeholder="What areas could be improved?"
                        variant="outlined"
                        sx={{ backgroundColor: 'background.paper' }}
                        InputProps={{
                          readOnly: isReadOnlyView
                        }}
                      />
                    </Grid>

                    {/* Recommendations */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Recommendations"
                        value={feedback.recommendations}
                        onChange={(e) => !isReadOnlyView && setFeedback(prev => ({ ...prev, recommendations: e.target.value }))}
                        placeholder="Your recommendations for this candidate"
                        variant="outlined"
                        sx={{ backgroundColor: 'background.paper' }}
                        InputProps={{
                          readOnly: isReadOnlyView
                        }}
                      />
                    </Grid>

                    {/* Notes */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Additional Notes"
                        value={feedback.notes}
                        onChange={(e) => !isReadOnlyView && setFeedback(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Any additional feedback or observations"
                        variant="outlined"
                        sx={{ backgroundColor: 'background.paper' }}
                        InputProps={{
                          readOnly: isReadOnlyView
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={() => setReviewDialogOpen(false)} color="inherit" sx={{ minWidth: 100 }}>
            {isReadOnlyView ? 'Close' : 'Cancel'}
          </Button>
          {!isReadOnlyView && (
            <Button
              variant="contained"
              onClick={handleSubmitFeedback}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : <StarIcon />}
              sx={{ minWidth: 160 }}
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ===== FEEDBACK DISPLAY DIALOG ===== */}
      <Dialog
        open={feedbackDisplayDialogOpen}
        onClose={() => setFeedbackDisplayDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {(() => {
            const isInterviewFeedback = selectedFeedback && selectedFeedback.final_verdict !== undefined;
            return (
              <>
                <Typography variant="h6">{isInterviewFeedback ? 'Interview Feedback' : 'CV Review Feedback'}</Typography>
                {selectedFeedback && (
                  <Typography variant="subtitle2" color="text.secondary">
                    {selectedFeedback.candidate_name || selectedFeedback.candidate?.name} - {selectedFeedback.job_title}
                  </Typography>
                )}
              </>
            );
          })()}
        </DialogTitle>
        <DialogContent dividers>
          {selectedFeedback && (
            <Box>
              {/* Candidate Information Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <People sx={{ fontSize: 20 }} />
                    Candidate Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography><strong>Name:</strong> {selectedFeedback.candidate_name}</Typography>
                      <Typography><strong>Email:</strong> {selectedFeedback.candidate_email}</Typography>
                      <Typography><strong>Phone:</strong> {selectedFeedback.candidate_phone || 'Not provided'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography><strong>Experience:</strong> {selectedFeedback.candidate_experience_years || 0} years</Typography>
                      <Typography><strong>Job Title:</strong> {selectedFeedback.job_title}</Typography>
                      <Typography><strong>Vendor:</strong> {selectedFeedback.vendor_name}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Overall Rating Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  {(() => {
                    const isInterviewFeedback = selectedFeedback && selectedFeedback.final_verdict !== undefined;
                    // ✅ FIXED: Create a single variable to hold the correct overall rating
const overallRating = isInterviewFeedback ? selectedFeedback.overall_rating || 0 : selectedFeedback.rating || 0
                    return (
                      <>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StarIcon sx={{ fontSize: 20 }} />
                          Overall Assessment
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <Typography variant="subtitle1"><strong>Overall Rating:</strong></Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {/* ✅ FIXED: Use the correct 'overallRating' variable */}
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <StarIcon
                                    key={star}
                                    sx={{
                                      color: star <= overallRating ? 'warning.main' : 'grey.300',
                                      fontSize: 16
                                    }}
                                  />
                                ))}
                                <Typography key="rating-text" variant="body2" sx={{ ml: 1 }}>
                                  ({overallRating}/5)
                                </Typography>
                              </Box>
                            </Box>
                            <Typography><strong>Status:</strong>
                              <Chip
                                size="small"
                                label={isInterviewFeedback ? selectedFeedback.final_verdict || 'pending' : selectedFeedback.status || 'pending'}
                                color={isInterviewFeedback ?
                                  (selectedFeedback.final_verdict === 'hire' ? 'success' : selectedFeedback.final_verdict === 'reject' ? 'error' : 'default') :
                                  (selectedFeedback.status === 'approved' ? 'success' : selectedFeedback.status === 'rejected' ? 'error' : 'default')
                                }
                                variant="filled"
                                sx={{ ml: 1 }}
                              />
                            </Typography>
                            <Typography><strong>{isInterviewFeedback ? 'Completed' : 'Submitted'}:</strong> {formatDate(isInterviewFeedback ? selectedFeedback.completed_at : selectedFeedback.submitted_at)}</Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" gutterBottom><strong>Detailed Ratings:</strong></Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {isInterviewFeedback ? (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Technical:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{selectedFeedback.technical_rating || 0}/5</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Communication:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{selectedFeedback.communication_rating || 0}/5</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Problem Solving:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{selectedFeedback.problem_solving_rating || 0}/5</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Cultural Fit:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{selectedFeedback.cultural_fit_rating || 0}/5</Typography>
                                  </Box>
                                </>
                              ) : (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Experience Relevance:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{selectedFeedback.experience_relevance || 0}/5</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Education Fit:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{selectedFeedback.education_fit || 0}/5</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Domain Knowledge:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{selectedFeedback.domain_knowledge || 0}/5</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Overall Impression:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{selectedFeedback.overall_impression || 0}/5</Typography>
                                  </Box>
                                </>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Feedback Details Cards */}
              <Grid container spacing={3}>
                {/* Strengths */}
                {selectedFeedback.strengths && (
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle sx={{ fontSize: 20 }} />
                          Strengths
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedFeedback.strengths}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Areas for Improvement */}
                {(() => {
                  const isInterviewFeedback = selectedFeedback && selectedFeedback.final_verdict !== undefined;
                  const areasText = isInterviewFeedback ? selectedFeedback.areas_for_improvement : selectedFeedback.weaknesses;
                  return areasText && (
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Warning sx={{ fontSize: 20 }} />
                            Areas for Improvement
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {areasText}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })()}

                {/* Recommendations */}
                {selectedFeedback.recommendations && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'info.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Lightbulb sx={{ fontSize: 20 }} />
                          Recommendations
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedFeedback.recommendations}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Hashtags */}
                {(() => {
                  const hashtags = selectedFeedback.hashtags_list || selectedFeedback.hashtags || [];
                  return (
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Lightbulb sx={{ fontSize: 20 }} />
                            Associated Hashtags
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {hashtags.length > 0 ? (
                              hashtags.map((hashtag, index) => (
                                <Chip
                                  key={index}
                                  label={`#${hashtag}`}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No hashtags associated with this feedback.
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })()}

                {/* Additional Notes */}
                {(() => {
                  const isInterviewFeedback = selectedFeedback && selectedFeedback.final_verdict !== undefined;
                  const notesText = isInterviewFeedback ? selectedFeedback.feedback_notes : selectedFeedback.notes;
                  return notesText && (
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Notes sx={{ fontSize: 20 }} />
                            Additional Notes
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {notesText}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })()}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={() => setFeedbackDisplayDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={5000}
        onClose={() => setAlert({ open: false, message: '', severity: 'info' })}
      >
        <Alert severity={alert.severity} onClose={() => setAlert({ open: false, message: '', severity: 'info' })}>
          {alert.message}
        </Alert>
      </Snackbar>
    </AppShell>
  );
};

export default PanelistDashboard;
